/**
 * Closing a panel by clicking away from it.
 *
 * Sounds like one line, and was written as one in five places - close when
 * the click lands on the backdrop rather than the panel. That is wrong in a
 * way you only find by using it: select some text in a dialog, drag past its
 * edge, let go, and it shuts. A `click` is dispatched on the nearest common
 * ancestor of where the button went down and where it came up, so a drag that
 * starts inside and ends outside delivers a click straight to the backdrop,
 * indistinguishable from a click that was always out there.
 *
 * So both halves have to land on the backdrop. Dragging out of a panel and
 * releasing now does nothing at all, which is what everyone expects, and
 * pressing outside and dragging back in does nothing either.
 */

import { useRef, type MouseEvent } from 'react';

export type BackdropProps = {
  onMouseDown: (e: MouseEvent) => void;
  onMouseUp: (e: MouseEvent) => void;
};

/**
 * Props for the backdrop element.
 *
 * Spread onto whichever element is the scrim. The panel must be inside it -
 * that is what makes "the target is the backdrop itself" mean "not on the
 * panel".
 */
export function useBackdropClose(onClose: () => void): BackdropProps {
  /* Where the press started. Kept in a ref because nothing renders on it. */
  const armed = useRef(false);

  return {
    onMouseDown(e) {
      // Left button only: a right-click opening a context menu is not a
      // request to close what is under it.
      armed.current = e.button === 0 && e.target === e.currentTarget;
    },
    onMouseUp(e) {
      const wasArmed = armed.current;
      armed.current = false;
      if (wasArmed && e.button === 0 && e.target === e.currentTarget) onClose();
    }
  };
}
